import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { UndoRedoHandler } from "src/app/shared/base/UndoRedoHandler";
import {
  LayoutUnit,
  LayoutUnitProps,
  LayoutProps,
  Layout,
  UnitPosition,
  UnitPositionProps,
} from "src/app/shared/Models/read";
import { CanvasLayout } from "../models/canvasLayout";
import { LayoutUnitTypeEnum } from "src/app/shared/Models/read/LayoutUnitType";

export interface BuildingLayoutServiceState {
  layout: LayoutProps;
  selectedUnitPosition: UnitPositionProps;
}

@Injectable()
export class BuildingLayoutService extends UndoRedoHandler<BuildingLayoutServiceState> {
  private $layout: BehaviorSubject<Layout> = new BehaviorSubject<Layout>(null);

  protected applyState(state: BuildingLayoutServiceState) {
    const layout = Layout.create(state.layout);
    this.$layout.next(layout);
    this.$selectedUnitPosition.next(
      UnitPosition.create(state.selectedUnitPosition)
    );
  }

  getState(): BuildingLayoutServiceState {
    return {
      layout: Layout.create(this.$layout.value),
      selectedUnitPosition: this.$selectedUnitPosition.value,
    };
  }

  loadLayout(layout: LayoutProps) {
    const selectedUnitPosition = { floorIndex: 0, unitIndex: 0 };
    this.applyState({ layout, selectedUnitPosition });
    this.dropChanges();
  }

  getLayout(): Observable<Layout> {
    return this.$layout;
  }

  decrementFloors() {
    this.snapped(() => {
      const layout = this.$layout.value;
      layout.popFloor();
      if (!layout.floors[this.$selectedUnitPosition.value.floorIndex])
        this.$selectedUnitPosition.next(
          UnitPosition.create({ floorIndex: 0, unitIndex: 0 })
        );

      this.$layout.next(layout);
    });
  }

  incrementFloors() {
    this.snapped(() => {
      const layout = this.$layout.value;
      layout.pushFloor();
      this.$layout.next(layout);
    });
  }

  private $selectedUnitPosition: BehaviorSubject<UnitPosition> =
    new BehaviorSubject<UnitPosition>(undefined);
  getSelectedUnitPosition(): Observable<UnitPosition> {
    return this.$selectedUnitPosition;
  }

  setSelectedUnitPosition(index: UnitPosition) {
    this.$selectedUnitPosition.next(index);
  }

  dropSelectedUnitPosition() {
    this.$selectedUnitPosition.next(undefined);
  }

  getFloorCount(): number {
    return this.$layout.value.floors.length;
  }

  getSelectedFloorUnitsCount(): number {
    const layout = this.$layout.value;
    const { floorIndex } = this.$selectedUnitPosition.value;
    return layout.floors[floorIndex]?.units.length;
  }

  getSelectedUnit(): LayoutUnit {
    const layout = this.$layout.value;
    const unitPosition = this.$selectedUnitPosition.value;
    return layout.getUnit(unitPosition);
  }

  updateSelectedUnit(props: LayoutUnitProps) {
    const unitPosition = this.$selectedUnitPosition.value;
    if (!unitPosition) return;

    const layout = this.$layout.value;
    const currentUnit = layout.getUnit(unitPosition);
    if (currentUnit.sameTo(props)) return;

    this.snapped(() => {
      layout.updateUnit(unitPosition, props);
      this.$layout.next(layout);
    });
  }

  decrementUnits() {
    const layout = this.$layout.value;
    const { floorIndex, unitIndex } = this.$selectedUnitPosition.value;
    this.snapped(() => {
      layout.popUnit(floorIndex);
      if (
        !layout.floors[this.$selectedUnitPosition.value.floorIndex].units[
          unitIndex
        ]
      )
        this.$selectedUnitPosition.next(
          UnitPosition.create({ floorIndex: 0, unitIndex: 0 })
        );

      this.$layout.next(layout);
    });
  }

  incrementUnits() {
    const layout = this.$layout.value;
    const { floorIndex } = this.$selectedUnitPosition.value;
    this.snapped(() => {
      layout.pushUnit(floorIndex);
      this.$layout.next(layout);
    });
  }

  private $draggingUnitType: BehaviorSubject<LayoutUnitTypeEnum> =
    new BehaviorSubject<LayoutUnitTypeEnum>(undefined);
  get draggingUnitTypeSubject(): Observable<LayoutUnitTypeEnum> {
    return this.$draggingUnitType;
  }

  get draggingUnitType() {
    return this.$draggingUnitType.value;
  }

  set draggingUnitType(value: LayoutUnitTypeEnum) {
    this.$draggingUnitType.next(value);
  }

  dropDraggingUnitType() {
    this.$draggingUnitType.next(undefined);
  }
}
